import type {
  ErrorResponse,
  SubscriptionPipelineTag as GeneratedSubscriptionPipelineTag,
  SubscriptionView,
} from "@/.generated/hey/subscriber-backend/types.gen";
import type {
  SubscriptionDataTableResult,
  SubscriptionHistoryQuery,
  SubscriptionModel,
  SubscriptionPipelineFailuresModel,
  SubscriptionPipelineTag,
  SubscriptionStatuses,
} from "@/domains/subscription/subscription.types";
import type { SiteId, SubscriptionId, SubscriptionPipelineId } from "@/types/common/ids";

import {
  addTagsOnSubscription,
  createDirectSubscription,
  getActiveSubscription,
  getLatestSubscription,
  getPipelineFailures,
  getPipelineTags,
  moveSiteToHouseHolder,
  removeTagsOnSubscription,
  reOpenSubscription,
  searchSiteSubscriptionsHistory,
} from "@/.generated/hey/subscriber-backend/sdk.gen";
import {
  zCreateDirectSubscriptionResponse,
  zGetPipelineFailuresResponse,
  zGetPipelineTagsResponse,
  zReOpenSubscriptionResponse,
  zSearchSiteSubscriptionsHistoryResponse,
  zSubscriptionView,
} from "@/.generated/hey/subscriber-backend/zod.gen";
import { handleSubscriberResult } from "@/api/api-result.ts";
import {
  _makeSubscriberBackendSdkClient,
  SubscriberBackendEnv,
} from "@/api/subscriber-backend/subscriber-backend-client-sdk.utils";
import {
  mapSubscriptionDataTable,
  mapSubscriptionPipelineFailures,
  mapSubscriptionPipelineTags,
  mapSubscriptionStatuses,
  mapSubscriptionView,
} from "@/api/subscriber-backend/subscription/subscription.mapping";
import { createBestEffortValidate } from "@/lib/validation-utils";

/**
 * Validate a response at runtime, but does not block the flow in case of validation failure.
 * @param validator - Zod schema to validate against
 * @param errorMessage - Optional custom error message for Sentry logging
 */
const bestEffortValidate = createBestEffortValidate("[Subscriber-backend] Response does not match OpenAPI contract");

export type SubscriberBackendClient = ReturnType<typeof makeSdkSubscriberBackendClient>;

export function makeSdkSubscriberBackendClient(env: SubscriberBackendEnv) {
  const { client } = _makeSubscriberBackendSdkClient(env);

  const requestOpts = {
    client,
    headers: {
      "Content-Type": "application/json",
    },
    throwOnError: false,
    responseStyle: "fields",
  } as const;

  return {
    site: {
      async moveToHouseholder(siteId: SiteId): Promise<void> {
        await moveSiteToHouseHolder({ ...requestOpts, path: { siteId } }).then(handleSubscriberResult);
      },
    },

    subscription: {
      async create(siteId: SiteId): Promise<SubscriptionModel> {
        return createDirectSubscription({ ...requestOpts, body: { siteId, tags: [] } })
          .then(handleSubscriberResult)
          .then(bestEffortValidate(zCreateDirectSubscriptionResponse))
          .then(mapSubscriptionView);
      },

      async getActive(siteId: SiteId): Promise<SubscriptionModel | null> {
        const response = await getActiveSubscription({
          ...requestOpts,
          path: { siteId },
        });

        return mapNullableSubscriptionViewResponse(response);
      },
      async getLatest(siteId: SiteId): Promise<SubscriptionModel | null> {
        const response = await getLatestSubscription({
          ...requestOpts,
          path: { siteId },
        });

        return mapNullableSubscriptionViewResponse(response);
      },

      async searchHistory(siteId: SiteId, query: SubscriptionHistoryQuery): Promise<SubscriptionDataTableResult> {
        return searchSiteSubscriptionsHistory({ ...requestOpts, path: { siteId }, body: query })
          .then(handleSubscriberResult)
          .then(bestEffortValidate(zSearchSiteSubscriptionsHistoryResponse))
          .then(mapSubscriptionDataTable);
      },

      async reOpen(subscriptionId: SubscriptionId): Promise<SubscriptionStatuses> {
        return reOpenSubscription({ ...requestOpts, path: { subscriptionId } })
          .then(handleSubscriberResult)
          .then(bestEffortValidate(zReOpenSubscriptionResponse))
          .then((r) => mapSubscriptionStatuses(r.statuses));
      },

      async addTags(subscriptionId: SubscriptionId, tagPaths: string[]): Promise<void> {
        await addTagsOnSubscription({ ...requestOpts, path: { subscriptionId }, body: { tags: tagPaths } }).then(
          handleSubscriberResult,
        );
      },

      async removeTags(subscriptionId: SubscriptionId, tagPaths: string[]): Promise<void> {
        await removeTagsOnSubscription({ ...requestOpts, path: { subscriptionId }, body: { tags: tagPaths } }).then(
          handleSubscriberResult,
        );
      },
    },

    subscriptionPipeline: {
      async getFailures(pipelineId: SubscriptionPipelineId): Promise<SubscriptionPipelineFailuresModel> {
        return getPipelineFailures({ ...requestOpts, path: { pipelineId } })
          .then(handleSubscriberResult)
          .then(bestEffortValidate(zGetPipelineFailuresResponse))
          .then(mapSubscriptionPipelineFailures);
      },

      async getTags(pipelineId: SubscriptionPipelineId): Promise<SubscriptionPipelineTag[]> {
        return (
          getPipelineTags({ ...requestOpts, path: { pipelineId } })
            .then(handleSubscriberResult)
            .then(bestEffortValidate(zGetPipelineTagsResponse))
            // The generated schema is typed as `AnyZodObject` because of its recursive `children`,
            // so its inferred output loses the shape the schema actually validates.
            .then((tags) => mapSubscriptionPipelineTags(tags as GeneratedSubscriptionPipelineTag[]))
        );
      },
    },
  };
}

// Utils

function mapNullableSubscriptionViewResponse(response: {
  data: void | SubscriptionView | undefined;
  error?: ErrorResponse;
  request: Request;
  response: Response;
}): SubscriptionModel | null {
  const {
    response: { status },
  } = response;

  if (status === 204) {
    return null;
  }

  const successfulResponse = handleSubscriberResult(response);
  const data = successfulResponse.data;
  if (!data) {
    throw new Error("Subscription response is unexpectedly empty");
  }

  return mapSubscriptionView(bestEffortValidate(zSubscriptionView)({ ...successfulResponse, data }));
}
